import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Form, Modal, Spinner, Table,
} from 'react-bootstrap';
import {
  BulkCreateRow,
  BulkPreviewRow,
  BulkRowInput,
  createBulkSkins,
  previewBulkSkins,
} from './bulkCharacterImportAPI';

type BulkCharacterImportModalProps = {
  show: boolean;
  bookId: number;
  onHide: () => void;
  /**
   * Whether the current user may target another creator's art. Non-admins are
   * scoped to their own uploads, so the owner column and the owner results are
   * hidden from them entirely.
   */
  isAdmin?: boolean;
  /** Called after at least one skin is created, so the page can refresh its list. */
  onCreated?: () => void;
};

/**
 * Column separator for the owner cell: a comma, or a tab so a column copied
 * straight out of a spreadsheet works. Neither character can appear in an
 * uploaded file name, so splitting on the first occurrence is safe.
 */
const OWNER_SEPARATOR = /[,\t]/;

/**
 * Split the textarea into trimmed, de-duplicated rows. Each line is a key id,
 * optionally followed by a separator and an art-owner user id
 * (`<key id>,<userId>`). The userId column is admin-only; the server ignores it
 * for non-admins and imports from their own art. Rows are de-duplicated on the
 * (keyId, owner) pair so the same key can target two owners.
 *
 * The owner cell is passed through verbatim rather than validated here: the
 * server is the one that knows whether the caller may use it at all, and it
 * reports a malformed id as an `invalid_user` skip. Dropping it client-side
 * would silently fall back to the caller's own art and report the misleading
 * `no_matches` instead.
 */
export const parseRows = (raw: string): BulkRowInput[] => {
  const seen = new Set<string>();
  const out: BulkRowInput[] = [];
  raw.split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }
    const separatorIndex = trimmed.search(OWNER_SEPARATOR);
    const keyId = (separatorIndex === -1 ? trimmed : trimmed.slice(0, separatorIndex)).trim();
    if (!keyId) {
      return;
    }
    const owner = separatorIndex === -1 ? '' : trimmed.slice(separatorIndex + 1).trim();
    const dedupeKey = `${keyId} ${owner}`;
    if (seen.has(dedupeKey)) {
      return;
    }
    seen.add(dedupeKey);
    out.push(owner ? { keyId, userId: owner } : { keyId });
  });
  return out;
};

const errorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error ?? err?.message ?? fallback;

/** Creator-facing wording for each server-side skip reason. */
const SKIP_REASONS: Record<string, string> = {
  no_matches: 'no art found for this key id',
  no_neutral: 'no neutral expression — add art named <key id>_neutral, or named exactly the key id',
  no_representative: 'no usable base image',
  invalid_user: 'invalid user id in the second column',
  create_failed: 'could not be created',
};

const describeSkip = (reason?: string): string => `skipped — ${
  (reason && SKIP_REASONS[reason]) ?? reason ?? 'unknown reason'
}`;

/** The art owner a row resolved to, for display. */
const describeOwner = (userId: number | null): string => (userId === null ? '—' : String(userId));

export function BulkCharacterImportModal({
  show,
  bookId,
  onHide,
  isAdmin = false,
  onCreated,
}: BulkCharacterImportModalProps) {
  const [text, setText] = useState('');
  const [isPlayer, setIsPlayer] = useState(false);
  const [preview, setPreview] = useState<BulkPreviewRow[] | null>(null);
  const [results, setResults] = useState<BulkCreateRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => parseRows(text), [text]);
  // A row without a `neutral` variant is rejected server-side, so don't count it
  // towards what the Create button promises.
  const isCreatable = (row: BulkPreviewRow) => row.hasRepresentative && row.hasNeutral;
  const creatableCount = preview ? preview.filter(isCreatable).length : 0;
  const skippedCount = preview ? preview.length - creatableCount : 0;
  // Rows whose art exists but has no neutral — the actionable subset of skips.
  const missingNeutralCount = preview
    ? preview.filter((row) => row.matchedCount > 0 && !row.hasNeutral).length
    : 0;
  // Rows where an expression slot matched more than one image (latest wins).
  const duplicateCount = preview
    ? preview.filter((row) => row.duplicateExpressions.length > 0).length
    : 0;

  const resetOutput = () => {
    setPreview(null);
    setResults(null);
    setError(null);
  };

  const runPreview = async () => {
    setError(null);
    setResults(null);
    setLoading(true);
    try {
      setPreview(await previewBulkSkins(bookId, rows));
    } catch (err) {
      setError(errorMessage(err, 'Preview failed'));
    } finally {
      setLoading(false);
    }
  };

  const runCreate = async () => {
    setError(null);
    setLoading(true);
    try {
      const created = await createBulkSkins(bookId, rows, isPlayer);
      setResults(created);
      if (created.some((row) => row.status === 'created')) {
        onCreated?.();
      }
    } catch (err) {
      setError(errorMessage(err, 'Create failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Bulk create characters</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {!results && (
          <>
            <Form.Group className="mb-2">
              <Form.Label>
                Key ids
                {' '}
                <small className="text-muted">
                  {isAdmin ? (
                    <>
                      (one per line; optionally
                      {' '}
                      <code>key id,user id</code>
                      {' '}
                      to import from another creator&apos;s art)
                    </>
                  ) : '(one per line)'}
                </small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder="Head045_MBody1_MHair4_Color2_RedGreen_MOutfitA"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  resetOutput();
                }}
              />
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="bulk-is-player"
              label="Mark all as player characters"
              checked={isPlayer}
              onChange={(e) => setIsPlayer(e.target.checked)}
            />
            <div className="text-muted small mt-1">{`${rows.length} unique row(s)`}</div>

            {preview && skippedCount > 0 && (
              <Alert variant={creatableCount === 0 ? 'danger' : 'warning'} className="mt-3 mb-0">
                {creatableCount === 0
                  ? `None of these ${skippedCount} row(s) can be created.`
                  : `${skippedCount} of ${preview.length} row(s) will be skipped.`}
                {missingNeutralCount > 0 && (
                  <div className="small mt-1">
                    {`${missingNeutralCount} has art but no neutral expression. `}
                    Every character needs one — it&apos;s what the game shows when a line doesn&apos;t
                    name an expression. Upload art named
                    {' '}
                    <code>&lt;key id&gt;_neutral</code>
                    {' or named exactly the key id, then preview again.'}
                  </div>
                )}
              </Alert>
            )}

            {preview && duplicateCount > 0 && (
              <Alert variant="info" className="mt-3 mb-0 small">
                {`${duplicateCount} row(s) matched more than one image for the same expression. `}
                The most recently uploaded image is used for each; disable the older art if that is
                wrong.
              </Alert>
            )}

            {preview && (
              <Table size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Key id</th>
                    {isAdmin && <th>Owner</th>}
                    <th>Expressions matched</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row) => (
                    <tr
                      key={`${row.keyId} ${row.userId ?? ''}`}
                      className={isCreatable(row) ? '' : 'table-warning'}
                    >
                      <td>{row.keyId}</td>
                      {isAdmin && <td>{describeOwner(row.userId)}</td>}
                      <td>
                        {row.matchedCount === 0 && (
                          row.otherOwners?.length
                            ? `no matches — this art belongs to user ${row.otherOwners.join(', ')}`
                            : 'no matches'
                        )}
                        {row.matchedCount > 0 && row.matchedCount}
                        {row.matchedCount > 0 && !row.hasNeutral && (
                          <span className="text-danger ml-2">
                            no neutral expression — will be skipped
                          </span>
                        )}
                        {row.duplicateExpressions.length > 0 && (
                          <span className="text-muted ml-2">
                            {`(${row.duplicateExpressions.length} duplicate expression(s), latest used)`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        {results && (
          <Table size="sm">
            <thead>
              <tr>
                <th>Key id</th>
                {isAdmin && <th>Owner</th>}
                <th>Result</th>
                <th>Saved alias</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr
                  key={`${row.keyId} ${row.userId ?? ''}`}
                  className={row.status === 'created' ? 'table-success' : 'table-warning'}
                >
                  <td>{row.keyId}</td>
                  {isAdmin && <td>{describeOwner(row.userId)}</td>}
                  <td>
                    {row.status === 'created'
                      ? `created (${row.expressionCount} expressions)`
                      : describeSkip(row.reason)}
                  </td>
                  <td>{row.savedAlias ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        {loading && <Spinner animation="border" size="sm" role="status" aria-label="loading" />}
        {results ? (
          <Button variant="secondary" onClick={onHide}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide} disabled={loading}>Cancel</Button>
            <Button
              variant="outline-primary"
              onClick={runPreview}
              disabled={loading || rows.length === 0}
            >
              Preview
            </Button>
            <Button
              variant="primary"
              onClick={runCreate}
              disabled={loading || !preview || creatableCount === 0}
            >
              {creatableCount ? `Create (${creatableCount})` : 'Create'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
